import { sharedPaths } from 'routes/constants/sharedPaths';

const REFERRAL_ONBOARDING_QUERY_KEY = 'spence_referral_onboarding_query';

export type ReferralOnboardingQuery = {
  code: string;
  step: string;
};

function getSessionStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.sessionStorage;
}

function readNonEmpty(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Persist referral landing query (`code` / `step`) for later Contact Support return. */
export function persistReferralOnboardingQuery(search: string | URLSearchParams): void {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  let params: URLSearchParams;
  if (typeof search === 'string') {
    const normalized = search.startsWith('?') ? search.slice(1) : search;
    params = new URLSearchParams(normalized);
  } else {
    params = search;
  }

  const code = readNonEmpty(params.get('code'));
  if (!code) {
    return;
  }

  const step = readNonEmpty(params.get('step')) ?? '1';
  const payload: ReferralOnboardingQuery = { code, step };
  storage.setItem(REFERRAL_ONBOARDING_QUERY_KEY, JSON.stringify(payload));
}

export function readReferralOnboardingQuery(): ReferralOnboardingQuery | null {
  const storage = getSessionStorage();
  if (!storage) {
    return null;
  }

  const raw = storage.getItem(REFERRAL_ONBOARDING_QUERY_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as ReferralOnboardingQuery;
    const code = readNonEmpty(parsed.code);
    if (!code) {
      return null;
    }

    return {
      code,
      step: readNonEmpty(parsed.step) ?? '1',
    };
  } catch {
    return null;
  }
}

export function clearReferralOnboardingQuery(): void {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  storage.removeItem(REFERRAL_ONBOARDING_QUERY_KEY);
}

/**
 * Public landing path with persisted referral `code` + `step`.
 * Returns bare landing when no referral was stored.
 */
export function buildPublicLandingReferralReturnTo(): string {
  const stored = readReferralOnboardingQuery();
  if (!stored) {
    return sharedPaths.publicLanding;
  }

  const params = new URLSearchParams();
  params.set('code', stored.code);
  params.set('step', stored.step);
  return `${sharedPaths.publicLanding}?${params.toString()}`;
}
